"use client"

import { useState, useEffect, useRef } from "react"
import { format, subMonths, getYear, getMonth, startOfMonth } from "date-fns"
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Layers, FileText } from "lucide-react"
import UsageReportPDF from "./UsageReportPDF"

// Array nama bulan untuk picker kustom
const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun", 
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
]

export default function UsageReports({
  selectedProject,
  selectedProjects = [],
  selectedBillingId,
  customerName = "",
  availableProjects = [],
}) {
  const [showPDFModal, setShowPDFModal] = useState(false)
  const [groupedRecentMonths, setGroupedRecentMonths] = useState({})
  
  // State untuk data yang akan dikirim ke PDF, untuk memastikan data konsisten
  const [pdfReportData, setPdfReportData] = useState(null)

  // Bulan terakhir yang SUDAH SELESAI (1 bulan sebelum bulan berjalan)
  const lastCompletedDate = subMonths(new Date(), 1)
  const maxYear = getYear(lastCompletedDate)

  // State untuk picker utama
  const [selectedMonthDate, setSelectedMonthDate] = useState(undefined)
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [pickerYear, setPickerYear] = useState(maxYear)
  const pickerRef = useRef(null)

  // Effective projects array
  const effectiveProjects = selectedProjects && selectedProjects.length > 0
    ? selectedProjects
    : (selectedProject ? [selectedProject] : [])

  // Cek apakah bulan sudah selesai (harus sebelum bulan berjalan / tidak boleh bulan berjalan atau masa depan)
  const isMonthCompleted = (year, monthIndex) => {
    const currentStartOfMonth = startOfMonth(new Date())
    const targetMonthDate = startOfMonth(new Date(year, monthIndex, 1))
    return targetMonthDate < currentStartOfMonth
  }

  useEffect(() => {
    // 1. Generate & Kelompokkan bulan terakhir yang SUDAH SELESAI (1 s/d 12 bulan lalu)
    const months = []
    for (let i = 1; i <= 12; i++) {
      const date = subMonths(new Date(), i)
      months.push({ date })
    }

    const grouped = months.reduce((acc, { date }) => {
      const year = getYear(date).toString()
      if (!acc[year]) acc[year] = []
      acc[year].push(date)
      return acc
    }, {})
    setGroupedRecentMonths(grouped)

    // 2. Handle klik di luar picker untuk menutupnya
    function handleClickOutside(event) {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsPickerOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Handler untuk picker kustom: LANGSUNG BUKA PDF
  const handleMonthSelectFromPicker = (monthIndex) => {
    if (!isMonthCompleted(pickerYear, monthIndex)) return
    const newDate = startOfMonth(new Date(pickerYear, monthIndex, 1))
    setSelectedMonthDate(newDate)
    setIsPickerOpen(false)

    const reportData = {
      month: format(newDate, 'yyyy-MM'),
      projects: effectiveProjects,
      project: effectiveProjects.length === 1 ? effectiveProjects[0] : null,
      billingId: selectedBillingId,
    }
    setPdfReportData(reportData)
    setShowPDFModal(true)
  }

  // Handler untuk daftar cepat: LANGSUNG BUKA PDF
  const handleQuickPickAndShowReport = (date) => {
    if (startOfMonth(date) >= startOfMonth(new Date())) return
    const reportData = {
      month: format(date, 'yyyy-MM'),
      projects: effectiveProjects,
      project: effectiveProjects.length === 1 ? effectiveProjects[0] : null,
      billingId: selectedBillingId,
    }
    setPdfReportData(reportData)
    setShowPDFModal(true)
    setSelectedMonthDate(date)
  }

  const handleClosePDF = () => {
    setShowPDFModal(false)
    setPdfReportData(null) // Reset data PDF setelah ditutup
  }
  
  const sortedYears = Object.keys(groupedRecentMonths).sort((a, b) => b - a)

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm p-3.5 sm:p-6 flex flex-col h-full">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h2 className="text-sm sm:text-base font-semibold text-slate-900">Usage Reports</h2>
          {effectiveProjects.length > 1 ? (
            <span className="text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
              {effectiveProjects.length} Projects
            </span>
          ) : effectiveProjects.length === 1 ? (
            <span className="text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-semibold bg-blue-50 text-blue-700 border border-blue-200 truncate max-w-[140px]" title={effectiveProjects[0]}>
              {effectiveProjects[0]}
            </span>
          ) : (
            <span className="text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
              All Projects
            </span>
          )}
        </div>

        {/* --- Bagian Picker Kustom --- */}
        <div className="relative mb-4 sm:mb-6" ref={pickerRef}>
          <p className="text-xs sm:text-sm font-semibold text-slate-600 mb-1.5">Select a specific month</p>
          <button
            type="button"
            onClick={() => setIsPickerOpen(!isPickerOpen)}
            className="w-full flex items-center justify-between text-left py-1.5 sm:py-2.5 px-3 sm:px-4 bg-slate-50 border border-slate-200 rounded-lg transition-colors hover:bg-slate-100 hover:border-slate-300 cursor-pointer shadow-2xs min-h-[36px] sm:min-h-[42px]"
          >
            <span className={selectedMonthDate ? "text-slate-800 font-medium text-xs sm:text-sm" : "text-slate-500 text-xs sm:text-sm"}>
              {selectedMonthDate ? format(selectedMonthDate, 'MMMM yyyy') : 'Choose a completed month...'}
            </span>
            <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 text-slate-500" />
          </button>

          {isPickerOpen && (
            <div className="absolute top-full mt-2 w-full max-w-xs bg-white shadow-xl rounded-xl z-20 p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <button
                  type="button"
                  onClick={() => setPickerYear(pickerYear - 1)}
                  className="p-1.5 rounded-full hover:bg-slate-100 text-slate-600 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="font-bold text-slate-800 text-base">{pickerYear}</span>
                <button
                  type="button"
                  onClick={() => setPickerYear(pickerYear + 1)}
                  disabled={pickerYear >= maxYear}
                  className={`p-1.5 rounded-full transition-colors ${
                    pickerYear >= maxYear
                      ? "text-slate-300 cursor-not-allowed"
                      : "hover:bg-slate-100 text-slate-600"
                  }`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {MONTH_NAMES.map((month, index) => {
                  const isCompleted = isMonthCompleted(pickerYear, index)
                  const isSelected =
                    selectedMonthDate &&
                    getMonth(selectedMonthDate) === index &&
                    getYear(selectedMonthDate) === pickerYear

                  return (
                    <button
                      key={month}
                      type="button"
                      disabled={!isCompleted}
                      onClick={() => handleMonthSelectFromPicker(index)}
                      className={`p-2 text-sm rounded-lg text-center transition-all ${
                        isSelected
                          ? "bg-blue-600 text-white font-semibold shadow-sm"
                          : isCompleted
                          ? "hover:bg-blue-50 text-slate-700 hover:text-blue-600 font-medium cursor-pointer"
                          : "text-slate-300 bg-slate-50/50 cursor-not-allowed opacity-40 font-normal"
                      }`}
                      title={isCompleted ? `${month} ${pickerYear}` : `${month} ${pickerYear} (Not completed)`}
                    >
                      {month}
                    </button>
                  )
                })}
              </div>
              <div className="mt-3 pt-2.5 border-t border-slate-100 text-center">
                <span className="text-[11px] text-slate-400">Only completed months are available</span>
              </div>
            </div>
          )}
        </div>
      
        {/* Kontainer utama yang bisa di-scroll dengan tinggi maksimal */}
        <div className="flex-1 overflow-y-auto pr-1 sm:pr-2 custom-scrollbar max-h-[180px] sm:max-h-[200px]">
          {/* --- Bagian Pilihan Cepat (Dikelompokkan & Aksi Langsung) --- */}
          <div className="space-y-3 sm:space-y-4 mb-3 sm:mb-6">
            <p className="text-xs sm:text-sm font-semibold text-slate-600 border-t border-slate-100 pt-2.5 sm:pt-4">Or select a recent report</p>
            {sortedYears.map((year) => (
              <div key={year}>
                <h3 className="text-xs sm:text-sm font-semibold text-slate-700 mb-1.5">{year}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-2">
                  {groupedRecentMonths[year].map((date) => (
                    <button
                      key={format(date, 'yyyy-MM')}
                      type="button"
                      onClick={() => handleQuickPickAndShowReport(date)}
                      className="py-1.5 px-2 text-center rounded-lg transition-all duration-200 text-xs font-medium border border-slate-200 bg-white text-slate-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 cursor-pointer shadow-2xs min-h-[32px] sm:min-h-[36px]"
                    >
                      {format(date, "MMMM")}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* --- Keterangan Scope Laporan --- */}
        <div className="mt-auto pt-3 border-t border-slate-100">
          {effectiveProjects.length === 0 ? (
            <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-2 sm:p-2.5 text-center">
              <span className="text-[11px] sm:text-xs text-slate-500 block">Report Scope</span>
              <span className="text-xs font-bold text-slate-800">
                Entire Billing Account ({selectedBillingId || "All"})
              </span>
            </div>
          ) : effectiveProjects.length === 1 ? (
            <div className="bg-blue-50/70 border border-blue-200/80 rounded-xl p-2 sm:p-2.5 text-center">
              <span className="text-[11px] sm:text-xs text-blue-600 block">Report Scope (1 Project)</span>
              <span className="text-xs font-bold text-slate-800 truncate block" title={effectiveProjects[0]}>
                {effectiveProjects[0]}
              </span>
            </div>
          ) : (
            <div className="bg-indigo-50/70 border border-indigo-200/80 rounded-xl p-2 sm:p-2.5 text-center">
              <span className="text-[11px] sm:text-xs text-indigo-600 block">Multi-Project Report ({effectiveProjects.length} Projects)</span>
              <span className="text-xs font-bold text-indigo-950 block">
                Individual Project Pages + Combined Summary
              </span>
            </div>
          )}
        </div>
      </div>

      <UsageReportPDF
        isOpen={showPDFModal && Boolean(pdfReportData?.month)}
        onClose={handleClosePDF}
        selectedMonth={pdfReportData?.month}
        selectedProject={pdfReportData?.project}
        selectedProjects={pdfReportData?.projects || effectiveProjects}
        selectedBillingId={selectedBillingId}
        customerName={customerName}
      />
    </>
  )
}
